import { useTheme, ThemeKey } from "@/contexts/ThemeContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { Monitor, Terminal, Sun, FileText, Ruler, Building2, Eye } from "lucide-react";

const themes: { key: ThemeKey; label: string; desc: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { key: "modern-dark", label: "Modern Dark", desc: "Blue accents on dark grey — GitHub-style default", icon: Monitor },
  { key: "terminal-dark", label: "Terminal Dark", desc: "Green monospace, CRT aesthetic with scanlines", icon: Terminal },
  { key: "terminal-amber", label: "Amber CRT", desc: "Amber monospace, warm retro terminal", icon: Sun },
  { key: "paper", label: "Paper", desc: "Off-white typewriter style, clean and minimal", icon: FileText },
  { key: "blueprint", label: "Blueprint", desc: "Deep navy with cyan accents — engineering aesthetic", icon: Ruler },
  { key: "corporate-light", label: "Corporate Light", desc: "Clean white with soft blue — professional enterprise", icon: Building2 },
  { key: "high-contrast", label: "High Contrast", desc: "WCAG AAA — pure black with vivid yellow accents", icon: Eye },
];

export const AppearanceTab = () => {
  const { theme, setTheme, saving } = useTheme();

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Appearance</CardTitle>
          <CardDescription>Choose a visual theme. Changes apply instantly.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Label className="text-sm font-semibold">Theme</Label>
          <RadioGroup value={theme} onValueChange={(v) => setTheme(v as ThemeKey)} className="grid gap-3 sm:grid-cols-2">
            {themes.map((t) => (
              <label
                key={t.key}
                className={`relative flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors ${
                  theme === t.key ? "border-primary bg-primary/5" : "border-border hover:border-muted-foreground/30"
                }`}
              >
                <RadioGroupItem value={t.key} className="mt-0.5" />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <t.icon className="h-4 w-4" />
                    <span className="text-sm font-medium">{t.label}</span>
                    {t.key === "modern-dark" && <Badge variant="secondary" className="text-[10px] px-1.5 py-0">default</Badge>}
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">{t.desc}</p>
                </div>
              </label>
            ))}
          </RadioGroup>
          {saving && <p className="text-xs text-muted-foreground">Saving preference…</p>}

          <div className="pt-4 border-t">
            <Label className="text-sm font-semibold">Typography</Label>
            <p className="mt-1 text-xs text-muted-foreground">
              Tables and data views use <span className="font-mono">IBM Plex Mono</span> for all numeric values and codes regardless of theme.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
